import { Badge, Card, CardContent, CardHeader, CardTitle } from "@/ui";
import { Eye, Lightbulb, Globe, TrendingUp } from "lucide-react";
const visionImage = "/assets/website/images/Future_healthcare_AI_vision_8e28197b.png";

export default function OurVision() {
  return (
    <div>
      <main className="py-20">
        {/* Hero Image Section */}
        <div className="relative h-[600px] mb-20 overflow-hidden">
          <img 
            src={visionImage} 
            alt="Future Healthcare AI Vision"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
          <div className="absolute inset-0 flex items-center justify-center py-8">
            <div className="text-center max-w-5xl mx-auto px-4">
              <Badge variant="secondary" className="mb-4" data-testid="badge-vision">
                Our Vision on Healthcare
              </Badge>
              <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4" data-testid="text-vision-title">
                Reimagining Healthcare Through Intelligent Automation
              </h1>
              <div className="max-w-5xl mx-auto space-y-4" data-testid="text-vision-description">
                <p className="text-lg text-foreground leading-relaxed">
                  Harnessing the power of Agentic AI, our mission is to fundamentally transform healthcare delivery by providing intelligent, action-oriented solutions that resolve core industry challenges.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-background/50 backdrop-blur-sm rounded-lg p-6 border border-primary/20">
                    <h3 className="text-lg font-semibold text-primary mb-3">Empowering Healthcare Providers</h3>
                    <p className="text-foreground text-sm leading-relaxed">
                      We empower providers and healthcare organizations by deploying AI Agents that streamline clinical workflows, automate administrative burdens like prior authorization and documentation, and dramatically reduce clinician burnout.
                    </p>
                  </div>
                  
                  <div className="bg-background/50 backdrop-blur-sm rounded-lg p-6 border border-primary/20">
                    <h3 className="text-lg font-semibold text-primary mb-3">Patient-Centric Value-Based Care</h3>
                    <p className="text-foreground text-sm leading-relaxed">
                      This commitment extends beyond efficiency to enable seamless, patient-centric value-based care by integrating proactive wellness, personalized interventions, and predictive intelligence.
                    </p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-6 border border-primary/20">
                  <h3 className="text-lg font-semibold text-primary mb-3 text-center">Responsible AI Principles</h3>
                  <p className="text-foreground text-sm leading-relaxed text-center">
                    Crucially, our system is designed and deployed under the strictest principles of Responsible AI, ensuring fairness, transparency, and accountability to maintain patient trust and clinical integrity in every innovation we introduce.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto px-4">

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <Card className="hover-elevate text-center" data-testid="card-future-care">
              <CardHeader>
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Eye className="h-8 w-8 text-primary" />
                  <CardTitle>Future of Healthcare</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Healthcare will be transformed by intelligent agents that understand clinical context, 
                  anticipate needs, and seamlessly integrate into existing workflows.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    Zero-burden documentation
                  </li>
                  <li className="flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    Predictive clinical insights
                  </li>
                  <li className="flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    Personalized patient engagement
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover-elevate text-center" data-testid="card-innovation">
              <CardHeader>
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Lightbulb className="h-8 w-8 text-primary" />
                  <CardTitle>Innovation Philosophy</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  We believe innovation should enhance human capabilities, not replace them. 
                  Our AI solutions amplify clinical expertise and compassion.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    Human-centered design
                  </li>
                  <li className="flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    Evidence-based development
                  </li>
                  <li className="flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                    Continuous learning
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <Card className="hover-elevate text-center" data-testid="card-global-impact">
              <CardHeader>
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Globe className="h-8 w-8 text-primary" />
                  <CardTitle>Global Healthcare Impact</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Our vision extends beyond individual healthcare systems to create a global network 
                  of intelligent healthcare delivery that improves outcomes worldwide.
                </p>
              </CardContent>
            </Card>

            <Card className="hover-elevate text-center" data-testid="card-sustainable">
              <CardHeader>
                <div className="flex items-center justify-center gap-3 mb-3">
                  <TrendingUp className="h-8 w-8 text-primary" />
                  <CardTitle>Sustainable Healthcare</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  By reducing administrative burden and improving efficiency, we're building 
                  a more sustainable healthcare system for future generations.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-center mb-6" data-testid="text-commitment-title">
              Our Commitment to Healthcare
            </h2>
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-lg text-muted-foreground mb-6">
                We are committed to developing AI solutions that respect the sacred trust between 
                clinicians and patients while advancing the science of healthcare delivery.
              </p>
              <div className="grid md:grid-cols-3 gap-6 text-sm text-center">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Privacy First</h3>
                  <p className="text-muted-foreground">Patient data protection is fundamental to our design</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Clinician Empowerment</h3>
                  <p className="text-muted-foreground">Enhancing human decision-making, not replacing it</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Continuous Improvement</h3>
                  <p className="text-muted-foreground">Learning from every interaction to serve better</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}